import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from '@react-google-maps/api';
import { io } from 'socket.io-client';
import axios from 'axios';
import { toast } from 'sonner';
import { LogOut, MapPin, Navigation, Clock, DollarSign, X, History, User, Search, MessageCircle, Menu, Settings, List, Locate, Map, Minimize2, Car, RefreshCw, Globe, ExternalLink, Mail, HelpCircle, Pencil, AtSign, Phone, Check, Repeat, Calendar, Trash2, Plus, Route } from 'lucide-react';
import ChatBox from '@/components/ChatBox';
import NotificationCenter from '@/components/NotificationCenter';
import { useLocationBackground } from '@/components/LocationBackground';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SOCKET_URL = process.env.REACT_APP_BACKEND_URL;
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const libraries = ['places'];

// Google Maps dark theme styles
const mapStyles = [
  { elementType: 'geometry', stylers: [{ color: '#242f3e' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#242f3e' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] },
  { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#d59563' }] },
  { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#d59563' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#263c3f' }] },
  { featureType: 'poi.park', elementType: 'labels.text.fill', stylers: [{ color: '#6b9a76' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#38414e' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#212a37' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#9ca5b3' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#746855' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#1f2835' }] },
  { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#f3d19c' }] },
  { featureType: 'transit', elementType: 'geometry', stylers: [{ color: '#2f3948' }] },
  { featureType: 'transit.station', elementType: 'labels.text.fill', stylers: [{ color: '#d59563' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#17263c' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#515c6d' }] },
  { featureType: 'water', elementType: 'labels.text.stroke', stylers: [{ color: '#17263c' }] },
];

// Calculate distance in miles using Haversine formula
const calculateDistance = (lat1, lng1, lat2, lng2) => {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng/2) * Math.sin(dLng/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  const distanceKm = R * c;
  return distanceKm / 1.6;
};

// Calculate default fare: $3 min for <3 miles, $1/mile rounded up, +$1 per extra seat
const calculateFare = (distance, seats = 1) => {
  let baseFare;
  if (distance < 3) {
    baseFare = 3;
  } else {
    baseFare = Math.ceil(distance);
  }
  const extraSeats = Math.max(0, seats - 1);
  return Math.round(baseFare + extraSeats); // Always return whole number
};

export default function RiderDashboard() {
  const { user, logout, refreshUser, switchRole } = useAuth();
  const navigate = useNavigate();
  const { cityName } = useLocationBackground();
  const [activeRide, setActiveRide] = useState(null);
  const [rideHistory, setRideHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [driverLocation, setDriverLocation] = useState(null);
  const [showChat, setShowChat] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const [isEditingAccount, setIsEditingAccount] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [availableDrivers, setAvailableDrivers] = useState([]);
  const [fullScreenMap, setFullScreenMap] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [switchingRole, setSwitchingRole] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [chatRide, setChatRide] = useState(null); // For chat from notification center
  const menuRef = useRef(null); // Ref for click-outside detection
  const [accountForm, setAccountForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    username: '',
    phone: '',
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const socketRef = useRef(null);
  const driverFetchRef = useRef(null);
  const mapRef = useRef(null);

  // Google Maps loader
  const { isLoaded: mapsLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries,
  });

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  const handleSwitchRole = async () => {
    if (activeRide) {
      toast.error('Cannot switch roles while you have an active ride');
      return;
    }
    
    setSwitchingRole(true);
    try {
      const updatedUser = await switchRole();
      toast.success(`Switched to ${updatedUser.role} mode!`);
      navigate(`/${updatedUser.role}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to switch role');
    } finally {
      setSwitchingRole(false);
    }
  };

  const [requestForm, setRequestForm] = useState({
    pickup_address: '',
    pickup_lat: 37.6872,
    pickup_lng: -97.3301,
    dropoff_address: '',
    dropoff_lat: 37.7200,
    dropoff_lng: -97.2800,
    stops: [], // Intermediate stops: [{address, lat, lng, order}]
    notes: '',
    ride_type: 'now',
    scheduled_time: '',
    seats_requested: 1,
    custom_price: '',
    leg_fares: [], // Custom fares for each leg
    // Driver assignment (for scheduled rides)
    assigned_driver_username: '',
    // Recurring ride options
    is_recurring: false,
    recurrence_type: 'custom', // 'daily', 'weekly', 'custom'
    recurrence_days: [], // [0,1,2,3,4,5,6] for Sun-Sat
    recurrence_end_type: 'never', // 'never', 'date', 'occurrences'
    recurrence_end_date: '',
    recurrence_occurrences: 10,
  });

  const [pickupSuggestions, setPickupSuggestions] = useState([]);
  const [dropoffSuggestions, setDropoffSuggestions] = useState([]);
  const [stopSuggestions, setStopSuggestions] = useState([]); // Suggestions for stop input
  const [activeStopIndex, setActiveStopIndex] = useState(-1); // Which stop input is active
  const [showPickupSuggestions, setShowPickupSuggestions] = useState(false);
  const [showDropoffSuggestions, setShowDropoffSuggestions] = useState(false);
  const [showStopSuggestions, setShowStopSuggestions] = useState(false);
  const [driverSearchResults, setDriverSearchResults] = useState([]);
  const [showDriverSuggestions, setShowDriverSuggestions] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [driverSearchLoading, setDriverSearchLoading] = useState(false);
  const [driverSearchQuery, setDriverSearchQuery] = useState('');
  const driverSearchTimeoutRef = useRef(null);
  const pickupTimeoutRef = useRef(null);
  const dropoffTimeoutRef = useRef(null);
  const stopTimeoutRef = useRef(null);

  const [mapCenter, setMapCenter] = useState([37.6872, -97.3301]);

  // Recurring rides management
  const [showRecurringRides, setShowRecurringRides] = useState(false);
  const [recurringRides, setRecurringRides] = useState([]);

  // Handler for opening chat from notification center
  const handleOpenChatFromNotification = (ride) => {
    setChatRide(ride);
    setShowChat(true);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if clicking on the menu button itself
      const menuButton = document.querySelector('[data-testid="hamburger-menu-btn"]');
      if (menuButton && menuButton.contains(event.target)) {
        return;
      }
      
      if (showMenu && menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      // Small delay to prevent immediate close on the same click that opened it
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);
      
      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showMenu]);

  useEffect(() => {
    fetchActiveRide();
    fetchRideHistory();
    fetchAvailableDrivers();
    fetchRecurringRides();

    // Set initial account form data
    const names = user.name?.split(' ') || ['', ''];
    setAccountForm({
      first_name: names[0] || '',
      last_name: names.slice(1).join(' ') || '',
      email: user.email || '',
      username: user.username || '',
      phone: user.phone || '',
      current_password: '',
      new_password: '',
      confirm_password: '',
    });

    // Get user's current location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setUserLocation({
            latitude,
            longitude
          });
          setMapCenter([latitude, longitude]);
          // Update the request form's pickup location to user's actual location
          setRequestForm(prev => ({
            ...prev,
            pickup_lat: latitude,
            pickup_lng: longitude
          }));
          console.log('Rider location initialized:', latitude, longitude);
        },
        (error) => {
          console.warn('Geolocation error:', error.message);
          // Default to Wichita if location access denied - don't show error toast on initial load
          setUserLocation({ latitude: 37.6872, longitude: -97.3301 });
          setMapCenter([37.6872, -97.3301]);
        },
        {
          enableHighAccuracy: false, // Try low accuracy first for faster response
          timeout: 15000,
          maximumAge: 300000 // Cache for 5 minutes
        }
      );
    } else {
      // Geolocation not supported
      setUserLocation({ latitude: 37.6872, longitude: -97.3301 });
      setMapCenter([37.6872, -97.3301]);
    }

    // Fetch available drivers every 10 seconds
    driverFetchRef.current = setInterval(fetchAvailableDrivers, 10000);

    // Setup Socket.IO
    socketRef.current = io(SOCKET_URL, {
      transports: ['websocket', 'polling'],
    });

    socketRef.current.on('connect', () => {
      console.log('Connected to socket');
      socketRef.current.emit('join', { user_id: user.id, role: user.role });
    });

    socketRef.current.on('ride_update', (ride) => {
      console.log('Ride update:', ride);
      
      // Check if this ride update is for the current rider
      const isRiderRide = ride.rider_id === user.id;
      
      // End ride when arrived, completed, or cancelled - reset to default state
      if (ride.status === 'arrived' || ride.status === 'completed' || ride.status === 'cancelled') {
        // Only process if this is the rider's ride
        if (isRiderRide) {
          const message = ride.status === 'arrived' 
            ? 'You have arrived at your destination!' 
            : `Ride ${ride.status}`;
          toast.success(message);
          
          // Reset all ride-related state to default (back to "Request a Ride" view)
          setActiveRide(null);
          setShowChat(false);
          setUnreadMessages(0);
          setDriverLocation(null);
          setShowHistory(false);
          setShowRequestForm(false);
          setShowCancelModal(false);
          setCancelReason('');
          setMapCenter([37.6872, -97.3301]); // Reset to default Wichita
          
          // Reset request form to defaults
          setRequestForm({
            pickup_address: '',
            pickup_lat: 37.6872,
            pickup_lng: -97.3301,
            dropoff_address: '',
            dropoff_lat: 37.7200,
            dropoff_lng: -97.2800,
            notes: '',
            ride_type: 'now',
            scheduled_time: '',
            seats_requested: 1,
            custom_price: '',
            stops: [],
            leg_fares: [],
          });
          setLegDetails([]);
          
          // Refresh data
          fetchRideHistory();
          fetchAvailableDrivers();
        }
      } else if (isRiderRide) {
        // Update active ride for any status change (accepted, en_route, etc.)
        setActiveRide(ride);
        if (ride.status === 'accepted') {
          toast.success('Driver accepted! You can now chat with your driver.');
        }
      }
    });

    socketRef.current.on('driver_location', (location) => {
      console.log('Driver location update:', location);
      setDriverLocation(location);
    });

    // Listen for new messages and show notification if chat is closed
    socketRef.current.on('new_message', (message) => {
      console.log('New message received:', message);
      // Only increment unread count if chat is not open and message is from driver
      if (message.sender_role === 'driver') {
        setUnreadMessages(prev => prev + 1);
      }
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
      if (driverFetchRef.current) {
        clearInterval(driverFetchRef.current);
      }
    };
  }, [user.id, user.role]);

  // Helper function to calculate distance between two points
  const calculateDistance = (lat1, lng1, lat2, lng2) => {
    const R = 3959; // Earth's radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  // Helper function to calculate fare for a distance
  const calculateFare = (distance, seats = 1) => {
    // Wichita area pricing: $3 min for <3 miles, else $1/mile rounded up
    let fare = distance < 3 ? 3 : Math.ceil(distance);
    // Add $1 for each seat beyond the first
    fare += Math.max(0, seats - 1);
    return fare;
  };

  // Calculate leg details for multi-stop rides
  const calculateLegDetails = useCallback(() => {
    if (!requestForm.pickup_address || !requestForm.dropoff_address) return [];
    
    const allPoints = [
      { address: requestForm.pickup_address, lat: requestForm.pickup_lat, lng: requestForm.pickup_lng, type: 'pickup' },
      ...requestForm.stops.map((s, i) => ({ ...s, type: 'stop', order: i + 1 })),
      { address: requestForm.dropoff_address, lat: requestForm.dropoff_lat, lng: requestForm.dropoff_lng, type: 'dropoff' }
    ];
    
    const legs = [];
    for (let i = 0; i < allPoints.length - 1; i++) {
      const from = allPoints[i];
      const to = allPoints[i + 1];
      const distance = calculateDistance(from.lat, from.lng, to.lat, to.lng);
      const fare = calculateFare(distance, requestForm.seats_requested);
      legs.push({
        leg: i + 1,
        from_address: from.address,
        to_address: to.address,
        distance: Math.round(distance * 10) / 10,
        fare: requestForm.leg_fares[i] || fare,
        defaultFare: fare
      });
    }
    return legs;
  }, [requestForm.pickup_address, requestForm.pickup_lat, requestForm.pickup_lng, 
      requestForm.dropoff_address, requestForm.dropoff_lat, requestForm.dropoff_lng,
      requestForm.stops, requestForm.seats_requested, requestForm.leg_fares]);

  const [legDetails, setLegDetails] = useState([]);

  // Auto-calculate and fill the price when locations or seats change
  useEffect(() => {
    if (requestForm.pickup_lat && requestForm.dropoff_lat && 
        requestForm.pickup_address && requestForm.dropoff_address) {
      const legs = calculateLegDetails();
      setLegDetails(legs);
      
      // Calculate total fare from all legs
      const totalFare = legs.reduce((sum, leg) => sum + leg.fare, 0);
      const totalDistance = legs.reduce((sum, leg) => sum + leg.distance, 0);
      
      setRequestForm(prev => ({
        ...prev,
        custom_price: String(totalFare),
        calculatedDistance: Math.round(totalDistance * 10) / 10
      }));
    }
  }, [requestForm.pickup_lat, requestForm.pickup_lng, requestForm.dropoff_lat, requestForm.dropoff_lng, 
      requestForm.seats_requested, requestForm.stops, requestForm.leg_fares, calculateLegDetails]);

  const fetchActiveRide = async () => {
    try {
      const response = await axios.get(`${API}/rides/active`);
      if (response.data) {
        setActiveRide(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch active ride:', error);
    }
  };

  const fetchRideHistory = async () => {
    try {
      const response = await axios.get(`${API}/rides/history`);
      setRideHistory(response.data);
    } catch (error) {
      console.error('Failed to fetch ride history:', error);
    }
  };

  const fetchAvailableDrivers = async () => {
    try {
      const response = await axios.get(`${API}/drivers/available`);
      setAvailableDrivers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch available drivers:', error);
    }
  };

  // Fetch recurring rides
  const fetchRecurringRides = async () => {
    try {
      const response = await axios.get(`${API}/rides/recurring`);
      setRecurringRides(response.data || []);
    } catch (error) {
      console.log('Recurring rides endpoint not available yet');
    }
  };

  // Delete a recurring ride
  const deleteRecurringRide = async (recurringId) => {
    try {
      await axios.delete(`${API}/rides/recurring/${recurringId}`);
      toast.success('Recurring ride cancelled');
      fetchRecurringRides();
    } catch (error) {
      toast.error('Failed to cancel recurring ride');
    }
  };

  // Get day name
  const getDayName = (dayNum) => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return days[dayNum];
  };

  // Toggle recurrence day
  const toggleRecurrenceDay = (day) => {
    const days = requestForm.recurrence_days;
    if (days.includes(day)) {
      setRequestForm({ ...requestForm, recurrence_days: days.filter(d => d !== day) });
    } else {
      setRequestForm({ ...requestForm, recurrence_days: [...days, day].sort() });
    }
  };

  // Add a new stop
  const addStop = () => {
    const newStop = {
      address: '',
      lat: null,
      lng: null,
      order: requestForm.stops.length + 1
    };
    setRequestForm(prev => ({
      ...prev,
      stops: [...prev.stops, newStop]
    }));
  };

  // Remove a stop
  const removeStop = (index) => {
    setRequestForm(prev => ({
      ...prev,
      stops: prev.stops.filter((_, i) => i !== index).map((s, i) => ({ ...s, order: i + 1 })),
      leg_fares: prev.leg_fares.filter((_, i) => i !== index && i !== index + 1)
    }));
  };

  // Update a stop's address
  const updateStopAddress = (index, value) => {
    const newStops = [...requestForm.stops];
    newStops[index] = { ...newStops[index], address: value };
    setRequestForm(prev => ({ ...prev, stops: newStops }));
  };

  // Update leg fare
  const updateLegFare = (legIndex, value) => {
    const newFares = [...requestForm.leg_fares];
    newFares[legIndex] = parseInt(value) || 0;
    setRequestForm(prev => ({ ...prev, leg_fares: newFares }));
  };

  // Google Places Autocomplete Service
  const autocompleteService = useRef(null);
  const placesService = useRef(null);

  // Initialize Google Places services when maps are loaded
  useEffect(() => {
    if (mapsLoaded && window.google) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
      // Create a dummy div for PlacesService (it requires a map or div element)
      const dummyDiv = document.createElement('div');
      placesService.current = new window.google.maps.places.PlacesService(dummyDiv);
    }
  }, [mapsLoaded]);

  const searchLocation = async (query, type, stopIndex = -1) => {
    if (!query || query.length < 3) {
      if (type === 'pickup') setPickupSuggestions([]);
      else if (type === 'dropoff') setDropoffSuggestions([]);
      else if (type === 'stop') setStopSuggestions([]);
      return;
    }

    if (!autocompleteService.current) {
      console.error('Google Places not loaded');
      return;
    }

    try {
      // Get user's current location for proximity-based search
      const centerLat = userLocation?.latitude || mapCenter[0] || 37.6872;
      const centerLng = userLocation?.longitude || mapCenter[1] || -97.3301;
      
      const request = {
        input: query,
        location: new window.google.maps.LatLng(centerLat, centerLng),
        radius: 80000, // 80km (~50 miles)
        componentRestrictions: { country: 'us' },
      };

      autocompleteService.current.getPlacePredictions(request, (predictions, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          const suggestions = predictions.map(prediction => ({
            display_name: prediction.description,
            place_id: prediction.place_id,
            lat: null, // Will be fetched when selected
            lon: null,
          }));

          if (type === 'pickup') {
            setPickupSuggestions(suggestions);
            setShowPickupSuggestions(true);
          } else if (type === 'dropoff') {
            setDropoffSuggestions(suggestions);
            setShowDropoffSuggestions(true);
          } else if (type === 'stop') {
            setStopSuggestions(suggestions);
            setActiveStopIndex(stopIndex);
            setShowStopSuggestions(true);
          }
        } else {
          if (type === 'pickup') setPickupSuggestions([]);
          else if (type === 'dropoff') setDropoffSuggestions([]);
          else if (type === 'stop') setStopSuggestions([]);
        }
      });
    } catch (error) {
      console.error('Failed to search location:', error);
    }
  };

  // Get place details (lat/lng) from place_id
  const getPlaceDetails = (placeId) => {
    return new Promise((resolve, reject) => {
      if (!placesService.current) {
        reject('Places service not loaded');
        return;
      }

      placesService.current.getDetails(
        { placeId, fields: ['geometry', 'formatted_address'] },
        (place, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
            resolve({
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng(),
              address: place.formatted_address,
            });
          } else {
            reject('Failed to get place details');
          }
        }
      );
    });
  };

  const handlePickupChange = (value) => {
    setRequestForm({ ...requestForm, pickup_address: value });
    
    if (pickupTimeoutRef.current) {
      clearTimeout(pickupTimeoutRef.current);
    }
    
    pickupTimeoutRef.current = setTimeout(() => {
      searchLocation(value, 'pickup');
    }, 500);
  };

  const handleDropoffChange = (value) => {
    setRequestForm({ ...requestForm, dropoff_address: value });
    
    if (dropoffTimeoutRef.current) {
      clearTimeout(dropoffTimeoutRef.current);
    }
    
    dropoffTimeoutRef.current = setTimeout(() => {
      searchLocation(value, 'dropoff');
    }, 500);
  };

  const selectPickupSuggestion = async (suggestion) => {
    try {
      // If we have place_id, get details from Google Places
      if (suggestion.place_id) {
        const details = await getPlaceDetails(suggestion.place_id);
        setRequestForm({
          ...requestForm,
          pickup_address: details.address || suggestion.display_name,
          pickup_lat: details.lat,
          pickup_lng: details.lng,
        });
        setMapCenter([details.lat, details.lng]);
      } else {
        // Fallback for suggestions with lat/lon already
        setRequestForm({
          ...requestForm,
          pickup_address: suggestion.display_name,
          pickup_lat: suggestion.lat,
          pickup_lng: suggestion.lon,
        });
        setMapCenter([suggestion.lat, suggestion.lon]);
      }
    } catch (error) {
      console.error('Failed to get place details:', error);
      toast.error('Failed to get location details');
    }
    setShowPickupSuggestions(false);
    setPickupSuggestions([]);
  };

  const selectDropoffSuggestion = async (suggestion) => {
    try {
      // If we have place_id, get details from Google Places
      if (suggestion.place_id) {
        const details = await getPlaceDetails(suggestion.place_id);
        setRequestForm({
          ...requestForm,
          dropoff_address: details.address || suggestion.display_name,
          dropoff_lat: details.lat,
          dropoff_lng: details.lng,
        });
      } else {
        // Fallback for suggestions with lat/lon already
        setRequestForm({
          ...requestForm,
          dropoff_address: suggestion.display_name,
          dropoff_lat: suggestion.lat,
          dropoff_lng: suggestion.lon,
        });
      }
    } catch (error) {
      console.error('Failed to get place details:', error);
      toast.error('Failed to get location details');
    }
    setShowDropoffSuggestions(false);
    setDropoffSuggestions([]);
  };

  // Handle stop address change with search
  const handleStopChange = (index, value) => {
    updateStopAddress(index, value);
    
    if (stopTimeoutRef.current) {
      clearTimeout(stopTimeoutRef.current);
    }
    
    stopTimeoutRef.current = setTimeout(() => {
      searchLocation(value, 'stop', index);
    }, 500);
  };

  // Select a stop suggestion
  const selectStopSuggestion = async (suggestion, index) => {
    try {
      if (suggestion.place_id) {
        const details = await getPlaceDetails(suggestion.place_id);
        const newStops = [...requestForm.stops];
        newStops[index] = {
          ...newStops[index],
          address: details.address || suggestion.display_name,
          lat: details.lat,
          lng: details.lng,
        };
        setRequestForm(prev => ({ ...prev, stops: newStops }));
      } else {
        const newStops = [...requestForm.stops];
        newStops[index] = {
          ...newStops[index],
          address: suggestion.display_name,
          lat: suggestion.lat,
          lng: suggestion.lon,
        };
        setRequestForm(prev => ({ ...prev, stops: newStops }));
      }
    } catch (error) {
      console.error('Failed to get place details:', error);
      toast.error('Failed to get location details');
    }
    setShowStopSuggestions(false);
    setStopSuggestions([]);
    setActiveStopIndex(-1);
  };

  const handleLocateMe = () => {
    if (navigator.geolocation) {
      toast.info('Getting your location...');
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          
          // Reverse geocode using Google Geocoder
          try {
            if (window.google) {
              const geocoder = new window.google.maps.Geocoder();
              geocoder.geocode({ location: { lat: latitude, lng: longitude } }, (results, status) => {
                if (status === 'OK' && results[0]) {
                  setRequestForm({
                    ...requestForm,
                    pickup_address: results[0].formatted_address,
                    pickup_lat: latitude,
                    pickup_lng: longitude,
                  });
                  setMapCenter([latitude, longitude]);
                  toast.success('Location found!');
                } else {
                  // Fallback to coordinates only
                  setRequestForm({
                    ...requestForm,
                    pickup_address: `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`,
                    pickup_lat: latitude,
                    pickup_lng: longitude,
                  });
                  setMapCenter([latitude, longitude]);
                  toast.success('Location set');
                }
              });
            }
          } catch (error) {
            console.error('Geocoding error:', error);
            setRequestForm({
              ...requestForm,
              pickup_lat: latitude,
              pickup_lng: longitude,
              pickup_address: `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`,
            });
            setUserLocation({ latitude, longitude });
            setMapCenter([latitude, longitude]);
            toast.success('Location set!');
          }
        },
        (error) => {
          let errorMessage = 'Unable to get your location.';
          switch(error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location access denied. Please allow location access in your browser settings.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location unavailable. Please check your device\'s location settings.';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out. Please try again.';
              break;
            default:
              errorMessage = 'Unable to get your location. Please enter your address manually.';
          }
          toast.error(errorMessage);
        },
        {
          enableHighAccuracy: false,
          timeout: 15000,
          maximumAge: 300000
        }
      );
    } else {
      toast.error('Geolocation is not supported by your browser. Please enter your address manually.');
    }
  };

  // Calculate default fare based on current form inputs
  const calculateDefaultFare = () => {
    if (!requestForm.pickup_lat || !requestForm.dropoff_lat) return '3';
    const distance = calculateDistance(
      requestForm.pickup_lat, 
      requestForm.pickup_lng, 
      requestForm.dropoff_lat, 
      requestForm.dropoff_lng
    );
    return String(calculateFare(distance, requestForm.seats_requested));
  };
 
  const handleUpdateAccount = async () => {
    // Validate required fields
    if (!accountForm.first_name.trim() || !accountForm.last_name.trim()) {
      toast.error('First name and last name are required');
      return;
    }
    
    if (!accountForm.current_password) {
      toast.error('Current password is required to save changes');
      return;
    }

    // Validate email format
    if (accountForm.email && !accountForm.email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    // Validate username
    if (accountForm.username && accountForm.username.trim().length < 3) {
      toast.error('Username must be at least 3 characters');
      return;
    }

    // Validate phone
    if (accountForm.phone && accountForm.phone.trim().length < 10) {
      toast.error('Please enter a valid phone number');
      return;
    }
    
    try {
      const updateData = {
        first_name: accountForm.first_name.trim(),
        last_name: accountForm.last_name.trim(),
        email: accountForm.email.trim(),
        username: accountForm.username.trim(),
        phone: accountForm.phone.trim(),
        current_password: accountForm.current_password,
      };
      
      if (accountForm.new_password) {
        if (accountForm.new_password !== accountForm.confirm_password) {
          toast.error('New passwords do not match');
          return;
        }
        if (accountForm.new_password.length < 6) {
          toast.error('New password must be at least 6 characters');
          return;
        }
        updateData.new_password = accountForm.new_password;
      }
      
      await axios.patch(`${API}/users/profile`, updateData);
      toast.success('Account updated successfully!');
      
      // Refresh user data to update UI immediately
      await refreshUser();
      
      // Update local form and clear passwords
      setAccountForm(prev => ({
        ...prev,
        current_password: '',
        new_password: '',
        confirm_password: '',
      }));
      
      setIsEditingAccount(false);
      setShowAccountSettings(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update account');
    }
  };

  // Driver search for assigning to scheduled rides
  const handleDriverSearch = async (query) => {
    setRequestForm(prev => ({ ...prev, assigned_driver_username: query }));
    setDriverSearchQuery(query);
    
    if (query.length < 2) {
      setDriverSearchResults([]);
      setShowDriverSuggestions(false);
      setDriverSearchLoading(false);
      return;
    }
    
    setDriverSearchLoading(true);
    
    // Debounce the search
    if (driverSearchTimeoutRef.current) {
      clearTimeout(driverSearchTimeoutRef.current);
    }
    
    driverSearchTimeoutRef.current = setTimeout(async () => {
      try {
        const response = await axios.get(`${API}/drivers/search?q=${encodeURIComponent(query)}`);
        setDriverSearchResults(response.data);
        setShowDriverSuggestions(true); // Always show dropdown when searching
        setDriverSearchLoading(false);
      } catch (error) {
        console.error('Driver search error:', error);
        setDriverSearchResults([]);
        setDriverSearchLoading(false);
      }
    }, 300);
  };

  const selectDriver = (driver) => {
    setSelectedDriver(driver);
    setRequestForm(prev => ({ ...prev, assigned_driver_username: driver.username }));
    setShowDriverSuggestions(false);
    setDriverSearchResults([]);
    setDriverSearchQuery('');
    toast.success(`${driver.name} (@${driver.username}) selected as your driver`);
  };

  const clearSelectedDriver = () => {
    setSelectedDriver(null);
    setRequestForm(prev => ({ ...prev, assigned_driver_username: '' }));
    setDriverSearchQuery('');
  };

  const handleRequestRide = async (e) => {
    e.preventDefault();
    
    // If there's an active ride, only allow scheduled rides
    if (activeRide && requestForm.ride_type === 'now') {
      toast.error('You already have an active ride. Please schedule for a different time.');
      return;
    }
    
    // Validate scheduled time if ride type is scheduled
    if (requestForm.ride_type === 'scheduled') {
      if (!requestForm.scheduled_time) {
        toast.error('Please select a date and time for your scheduled ride');
        return;
      }
      const scheduledDate = new Date(requestForm.scheduled_time);
      const now = new Date();
      if (scheduledDate <= now) {
        toast.error('Scheduled time must be in the future');
        return;
      }
    }
    
    // Validate stops have addresses and coordinates
    const validStops = requestForm.stops.filter(s => s.address && s.lat && s.lng);
    if (requestForm.stops.length > 0 && validStops.length !== requestForm.stops.length) {
      toast.error('Please select valid addresses for all stops');
      return;
    }
    
    try {
      // Prepare form data with custom_fare if provided
      const formData = { ...requestForm };
      if (requestForm.custom_price && parseInt(requestForm.custom_price) > 0) {
        formData.custom_fare = parseInt(requestForm.custom_price); // Whole number only
      }
      
      // Include stops and leg_fares
      formData.stops = validStops.map((s, i) => ({
        address: s.address,
        lat: s.lat,
        lng: s.lng,
        order: i + 1
      }));
      
      // Get custom leg fares if edited
      formData.leg_fares = legDetails.map((leg, i) => 
        requestForm.leg_fares[i] || leg.fare
      );
      
      // Include assigned driver username if selected
      if (selectedDriver && requestForm.ride_type === 'scheduled') {
        formData.assigned_driver_username = selectedDriver.username;
      }
      
      // Determine which endpoint to use
      let endpoint = `${API}/rides/request`;
      if (requestForm.is_recurring && requestForm.ride_type === 'scheduled') {
        endpoint = `${API}/rides/recurring`;
        formData.is_recurring = true;
        formData.recurrence_days = requestForm.recurrence_days;
        formData.recurrence_end_type = requestForm.recurrence_end_type;
        formData.recurrence_end_date = requestForm.recurrence_end_date;
        formData.recurrence_occurrences = requestForm.recurrence_occurrences;
      }
      
      const response = await axios.post(endpoint, formData);
      
      // Only set as active ride if it's an immediate ride and there's no current active ride
      if (requestForm.ride_type === 'now' && !activeRide) {
        setActiveRide(response.data);
      }
      
      setShowRequestForm(false);
      
      if (requestForm.is_recurring) {
        const ridesGenerated = response.data.rides_generated || 0;
        toast.success(`Recurring ride created! ${ridesGenerated} upcoming rides scheduled.`);
        fetchRecurringRides();
      } else if (requestForm.ride_type === 'scheduled') {
        if (selectedDriver) {
          toast.success(`Ride request sent to @${selectedDriver.username}! They'll be notified.`);
        } else {
          toast.success('Ride scheduled successfully! Check your email for confirmation.');
        }
        // Reset driver selection
        setSelectedDriver(null);
        setDriverSearchResults([]);
        // Refresh recurring rides list
        fetchRecurringRides();
      } else {
        toast.success('Ride requested! Finding a driver...');
      }
      
      // Reset form for next request
      setRequestForm({
        pickup_address: '',
        pickup_lat: userLocation?.latitude || 37.6872,
        pickup_lng: userLocation?.longitude || -97.3301,
        dropoff_address: '',
        dropoff_lat: 37.7200,
        dropoff_lng: -97.2800,
        stops: [],
        leg_fares: [],
        notes: '',
        ride_type: 'now',
        scheduled_time: '',
        seats_requested: 1,
        custom_price: '',
        is_recurring: false,
        recurrence_type: 'custom',
        recurrence_days: [],
        recurrence_end_type: 'never',
        recurrence_end_date: '',
        recurrence_occurrences: 10,
      });
      setLegDetails([]);
      
      if (!activeRide) {
        setMapCenter([requestForm.pickup_lat, requestForm.pickup_lng]);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request ride');
    }
  };

  // Open Google Maps with navigation route for rider
  const openGoogleMapsNavigation = () => {
    if (!activeRide) return;
    
    // For rider: show route from pickup to dropoff
    const url = `https://www.google.com/maps/dir/?api=1&origin=${activeRide.pickup_lat},${activeRide.pickup_lng}&destination=${activeRide.dropoff_lat},${activeRide.dropoff_lng}&travelmode=driving`;
    
    window.open(url, '_blank');
  };

  const handleCancelRide = async () => {
    if (!activeRide || !cancelReason.trim()) {
      toast.error('Please provide a reason for cancellation');
      return;
    }
    try {
      await axios.post(`${API}/rides/${activeRide.id}/cancel`, { reason: cancelReason });
      toast.success('Ride cancelled');
      setActiveRide(null);
      setShowCancelModal(false);
      setCancelReason('');
      fetchRideHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel ride');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      requested: 'text-yellow-400',
      accepted: 'text-blue-400',
      en_route: 'text-purple-400',
      arrived: 'text-green-400',
      in_progress: 'text-neon-cyan',
      completed: 'text-green-500',
      cancelled: 'text-red-400',
    };
    return colors[status] || 'text-white';
  };

  const formatStatus = (status) => {
    return status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  return (
    <div className="h-screen bg-dark-bg text-white relative overflow-hidden">
      {/* Map Background - Fixed */}
      <div className={`fixed inset-0 ${fullScreenMap ? 'z-50' : 'z-0'}`}>
        {mapsLoaded ? (
          <GoogleMap
            mapContainerStyle={{ height: '100%', width: '100%' }}
            center={{ lat: mapCenter[0], lng: mapCenter[1] }}
            zoom={13}
            onLoad={onMapLoad}
            options={{
              disableDefaultUI: !fullScreenMap,
              zoomControl: fullScreenMap,
              streetViewControl: false,
              mapTypeControl: false,
              fullscreenControl: false,
              styles: mapStyles,
            }}
          >
            {/* User's current location */}
            {userLocation && !activeRide && (
              <Marker 
                position={{ lat: userLocation.latitude, lng: userLocation.longitude }}
                icon={{ url: 'https://maps.google.com/mapfiles/ms/icons/purple-dot.png' }}
                title="Your Location"
              />
            )}
            
            {/* Available Drivers */}
            {!activeRide && availableDrivers.map((driver) => (
              <Marker
                key={driver.id}
                position={{ lat: driver.latitude, lng: driver.longitude }}
                icon={{ url: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png' }}
                title={`Driver: ${driver.name}`}
              />
            ))}
            
            {/* Active Ride Markers */}
            {activeRide && (
              <>
                <Marker 
                  position={{ lat: activeRide.pickup_lat, lng: activeRide.pickup_lng }}
                  icon={{ url: 'https://maps.google.com/mapfiles/ms/icons/green-dot.png' }}
                  title={`Pickup: ${activeRide.pickup_address}`}
                />
                <Marker 
                  position={{ lat: activeRide.dropoff_lat, lng: activeRide.dropoff_lng }}
                  icon={{ url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png' }}
                  title={`Dropoff: ${activeRide.dropoff_address}`}
                />
                {driverLocation && (
                  <Marker 
                    position={{ lat: driverLocation.latitude, lng: driverLocation.longitude }}
                    icon={{ url: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png' }}
                    title={`Driver: ${activeRide.driver_name}`}
                  />
                )}
              </>
            )}
          </GoogleMap>
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-dark-surface">
            <div className="text-neon-cyan animate-pulse">Loading Map...</div>
          </div>
        )}
        
        {/* Full Screen Map Close Button */}
        {fullScreenMap && (
          <div className="absolute top-4 right-4 z-[1000]">
            <button
              onClick={() => setFullScreenMap(false)}
              className="bg-dark-surface hover:bg-dark-subtle text-white p-3 rounded-full shadow-lg transition-all"
              data-testid="close-fullscreen-map-btn"
            >
              <Minimize2 className="w-6 h-6" />
            </button>
          </div>
        )}
        
        {/* Full Screen Map Legend */}
        {fullScreenMap && (
          <div className="absolute bottom-4 left-4 z-[1000] bg-dark-surface/90 backdrop-blur-sm rounded-xl p-4">
            <h4 className="text-white font-bold mb-2 text-sm">Map Legend</h4>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-purple-500"></div>
                <span className="text-white/80">Your Location</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-blue-500"></div>
                <span className="text-white/80">Available Drivers ({availableDrivers.length})</span>
              </div>
              {activeRide && (
                <>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-green-500"></div>
                    <span className="text-white/80">Pickup</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full bg-red-500"></div>
                    <span className="text-white/80">Dropoff</span>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Fixed Header - Always visible at top */}
      <div className="fixed top-0 left-0 right-0 z-50 px-4 sm:px-6 py-3 sm:py-4 pointer-events-auto bg-gradient-to-b from-dark-bg/95 to-transparent">
        <div className="glass-panel rounded-2xl p-3 sm:p-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors flex-shrink-0"
              data-testid="hamburger-menu-btn"
              title="Menu"
            >
              <Menu className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
            {/* Clickable Logo - navigates to home/dashboard */}
            <button
              onClick={() => navigate('/rider')}
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
              data-testid="logo-home-btn"
              title="Go to Dashboard"
            >
              <img src="/logo.png" alt="vgoride" className="w-8 h-8 sm:w-10 sm:h-10 object-contain" />
              <span className="font-outfit font-bold text-lg sm:text-xl text-neon-cyan hidden xs:inline">vgoride</span>
            </button>
            <div className="min-w-0 hidden sm:block ml-2">
              <div className="font-medium truncate">{user.name}</div>
              <div className="flex items-center gap-1 text-xs text-white/60">
                <Globe className="w-3 h-3" />
                <span className="truncate">{cityName}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            <button
              onClick={() => setFullScreenMap(true)}
              className="p-1.5 sm:p-2 hover:bg-white/10 rounded-lg transition-colors"
              data-testid="map-view-btn"
              title="Map View"
            >
              <Map className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
            {/* Chat Icon - Only visible during active ride */}
            {activeRide && activeRide.driver_name && (
              <button
                onClick={() => {
                  setShowChat(true);
                  setUnreadMessages(0);
                }}
                className="p-1.5 sm:p-2 hover:bg-white/10 rounded-lg transition-colors relative"
                data-testid="header-chat-btn"
                title="Chat with Driver"
              >
                <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                {unreadMessages > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 sm:w-5 sm:h-5 bg-red-500 text-white text-[10px] sm:text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
                    {unreadMessages > 9 ? '9+' : unreadMessages}
                  </span>
                )}
              </button>
            )}
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="p-1.5 sm:p-2 hover:bg-white/10 rounded-lg transition-colors"
              data-testid="history-btn"
              title="Ride History"
            >
              <History className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
            {/* Notification Center */}
            <NotificationCenter 
              userId={user.id} 
              userRole="rider" 
              onOpenChat={handleOpenChatFromNotification}
              token={localStorage.getItem('token')}
            />
            <button
              onClick={handleSwitchRole}
              disabled={switchingRole || activeRide}
              className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg transition-colors ${
                activeRide 
                  ? 'bg-white/5 text-white/40 cursor-not-allowed' 
                  : 'bg-purple-500/20 text-purple-400 hover:bg-purple-500/30'
              }`}
              data-testid="switch-role-btn"
              title={activeRide ? 'Complete active ride to switch' : 'Switch to Driver'}
            >
              {switchingRole ? (
                <RefreshCw className="w-3.5 h-3.5 sm:w-4 sm:h-4 animate-spin" />
              ) : (
                <Car className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              )}
              <span className="text-xs sm:text-sm font-medium hidden xs:inline">Drive</span>
            </button>
            <button
              onClick={logout}
              className="p-1.5 sm:p-2 hover:bg-white/10 rounded-lg transition-colors"
              data-testid="logout-btn"
              title="Sign Out"
            >
              <LogOut className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>
        </div>

        {/* Dropdown Menu */}
        {showMenu && (
          <div ref={menuRef} className="absolute top-20 left-6 w-64 glass-panel rounded-2xl p-2 z-[100] shadow-xl">
            <button
              onClick={() => {
                // Trigger notification center to open on My Rides tab
                const bellBtn = document.querySelector('[data-testid="notification-bell-btn"]');
                if (bellBtn) bellBtn.click();
                setTimeout(() => {
                  const myRidesTab = document.querySelector('[data-testid="my-rides-tab"]');
                  if (myRidesTab) myRidesTab.click();
                }, 100);
                setShowMenu(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/10 rounded-xl transition-colors text-left"
              data-testid="my-rides-menu-btn"
            >
              <Car className="w-5 h-5 text-neon-cyan" />
              <span>My Rides</span>
            </button>
            <button
              onClick={() => {
                setShowAccountSettings(true);
                setShowMenu(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/10 rounded-xl transition-colors text-left"
              data-testid="account-menu-btn"
            >
              <Settings className="w-5 h-5 text-neon-cyan" />
              <span>Account Settings</span>
            </button>
            <button
              onClick={() => {
                navigate('/rides-available');
                setShowMenu(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/10 rounded-xl transition-colors text-left"
              data-testid="all-rides-menu-btn"
            >
              <List className="w-5 h-5 text-neon-cyan" />
              <span>Rides Available</span>
            </button>
            <button
              onClick={() => {
                setShowRecurringRides(true);
                setShowMenu(false);
              }}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/10 rounded-xl transition-colors text-left"
              data-testid="recurring-rides-menu-btn"
            >
              <Repeat className="w-5 h-5 text-purple-400" />
              <span>Scheduled Rides</span>
              {recurringRides.length > 0 && (
                <span className="ml-auto bg-purple-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                  {recurringRides.length}
                </span>
              )}
            </button>
            <div className="border-t border-white/10 my-2"></div>
            <a
              href="mailto:customer-support@vgoride.net"
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/10 rounded-xl transition-colors text-left"
              data-testid="contact-support-btn"
            >
              <HelpCircle className="w-5 h-5 text-green-400" />
              <div>
                <span className="block">Contact Support</span>
                <span className="text-xs text-white/50">customer-support@vgoride.net</span>
              </div>
            </a>
          </div>
        )}
      </div>

      {/* Scrollable Content Area - with padding for fixed header */}
      <div className="relative z-10 min-h-screen overflow-y-auto pt-20 sm:pt-24 pb-20 overscroll-contain touch-pan-y">
      {/* Main Content */}
      <div className="relative z-0 px-4 sm:px-6 pb-6">
        {/* Request a Ride Button - Always visible when form is not shown */}
        {!showRequestForm && (
          <div className="max-w-md mx-auto mt-4 sm:mt-10">
            <button
              onClick={() => {
                // If there's an active ride, auto-select scheduled mode
                if (activeRide) {
                  setRequestForm(prev => ({ ...prev, ride_type: 'scheduled' }));
                }
                setShowRequestForm(true);
              }}
              className="w-full bg-neon-cyan text-black hover:bg-[#00B8D4] rounded-full font-bold py-4 sm:py-6 text-base sm:text-lg shadow-[0_0_20px_rgba(0,224,255,0.4)] transition-all hover:scale-105"
              data-testid="request-ride-btn"
            >
              {activeRide ? 'Schedule Another Ride' : 'Request a Ride'}
            </button>
            
            {/* Available Drivers Info */}
            {!activeRide && availableDrivers.length > 0 && (
              <div className="mt-4 glass-panel rounded-xl p-4 text-center">
                <div className="flex items-center justify-center gap-2 text-neon-cyan">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="font-medium">{availableDrivers.length} driver{availableDrivers.length !== 1 ? 's' : ''} nearby</span>
                </div>
                <button
                  onClick={() => setFullScreenMap(true)}
                  className="text-sm text-white/60 hover:text-white mt-1 underline"
                  data-testid="view-drivers-map-btn"
                >
                  View on map
                </button>
              </div>
            )}
          </div>
        )}

        {/* Request Form - Can be shown anytime */}
        {showRequestForm && (
          <div className="max-w-2xl mx-auto mt-2 sm:mt-6 pb-8">
            <div className="glass-panel rounded-2xl p-4 sm:p-6 max-h-[calc(100vh-8rem)] overflow-y-auto">
              <div className="flex items-center justify-between mb-4 sm:mb-6 sticky top-0 bg-dark-surface/95 backdrop-blur-sm py-2 -mx-4 px-4 sm:-mx-6 sm:px-6 z-10">
                <div>
                  <h2 className="font-outfit font-bold text-xl sm:text-2xl">
                    {activeRide ? 'Schedule Another Ride' : 'Request Ride'}
                  </h2>
                  {activeRide && (
                    <p className="text-sm text-white/60 mt-1">You can schedule rides for different times</p>
                  )}
                </div>
                <button
                  onClick={() => setShowRequestForm(false)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  data-testid="close-form-btn"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={handleRequestRide} className="space-y-3 sm:space-y-4">
                {/* Ride Type Selection */}
                <div>
                  <label className="block text-sm mb-2 sm:mb-3 text-white/80">When do you need a ride?</label>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3">
                    <button
                      type="button"
                      onClick={() => setRequestForm({ ...requestForm, ride_type: 'now', scheduled_time: '' })}
                      disabled={activeRide} // Disable "Now" if there's an active ride
                      className={`p-3 sm:p-4 rounded-xl border-2 transition-all ${
                        requestForm.ride_type === 'now'
                          ? 'border-neon-cyan bg-neon-cyan/10 text-neon-cyan'
                          : 'border-white/10 text-white/60 hover:border-white/20'
                      } ${activeRide ? 'opacity-50 cursor-not-allowed' : ''}`}
                      data-testid="ride-type-now-btn"
                    >
                      <Clock className="w-5 h-5 sm:w-6 sm:h-6 mx-auto mb-1 sm:mb-2" />
                      <div className="font-medium text-sm sm:text-base">Now</div>
                      <div className="text-[10px] sm:text-xs text-white/40 mt-0.5 sm:mt-1">
                        {activeRide ? 'Complete ride first' : 'Immediate pickup'}
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setRequestForm({ ...requestForm, ride_type: 'scheduled' })}
                      className={`p-3 sm:p-4 rounded-xl border-2 transition-all ${
                        requestForm.ride_type === 'scheduled'
                          ? 'border-neon-cyan bg-neon-cyan/10 text-neon-cyan'
                          : 'border-white/10 text-white/60 hover:border-white/20'
                      }`}
                      data-testid="ride-type-scheduled-btn"
                    >
                      <Clock className="w-5 h-5 sm:w-6 sm:h-6 mx-auto mb-1 sm:mb-2" />
                      <div className="font-medium text-sm sm:text-base">Schedule</div>
                      <div className="text-[10px] sm:text-xs text-white/40 mt-0.5 sm:mt-1">Choose date & time</div>
                    </button>
                  </div>
                </div>

                {/* Scheduled Time Picker */}
                {requestForm.ride_type === 'scheduled' && (
                  <div>
                    <label className="block text-sm mb-2 text-white/80">Select Date & Time</label>
                    <input
                      type="datetime-local"
                      required
                      value={requestForm.scheduled_time}
                      onChange={(e) => setRequestForm({ ...requestForm, scheduled_time: e.target.value })}
                      min={new Date().toISOString().slice(0, 16)}
                      className="w-full bg-dark-subtle border-transparent focus:border-neon-cyan focus:ring-1 focus:ring-neon-cyan rounded-xl p-3 text-white"
                      data-testid="input-scheduled-time"
                    />

                    {/* Assign a Specific Driver (Optional) */}
                    <div className="mt-4 p-4 bg-neon-cyan/10 border border-neon-cyan/20 rounded-xl">
                      <label className="block text-sm mb-2 text-neon-cyan font-medium flex items-center gap-2">
                        <User className="w-4 h-4" />
                        Request a Specific Driver (Optional)
                      </label>
                      
                      {selectedDriver ? (
                        <div className="flex items-center justify-between bg-neon-cyan/20 rounded-lg px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-neon-cyan/30 flex items-center justify-center text-neon-cyan font-bold">
                              {selectedDriver.name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <p className="font-medium text-white">{selectedDriver.name}</p>
                              <p className="text-xs text-white/60">@{selectedDriver.username}</p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={clearSelectedDriver}
                            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                            data-testid="clear-driver-btn"
                          >
                            <X className="w-4 h-4 text-white/60" />
                          </button>
                        </div>
                      ) : (
                        <div className="relative">
                          <div className="relative">
                            <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                            <input
                              type="text"
                              value={requestForm.assigned_driver_username}
                              onChange={(e) => handleDriverSearch(e.target.value)}
                              onFocus={() => setShowDriverSuggestions(driverSearchResults.length > 0)}
                              placeholder="Search by driver username..."
                              className="w-full bg-dark-subtle border-transparent focus:border-neon-cyan focus:ring-1 focus:ring-neon-cyan rounded-xl p-3 pl-10 text-white"
                              data-testid="input-driver-search"
                            />
                          </div>
                          
                          {/* Driver Search Results Dropdown */}
                          {showDriverSuggestions && driverSearchQuery.length >= 2 && (
                            <div className="absolute z-50 w-full mt-2 bg-dark-surface border border-neon-cyan/30 rounded-xl shadow-xl overflow-hidden max-h-64 overflow-y-auto">
                              {driverSearchLoading ? (
                                <div className="px-4 py-6 text-center">
                                  <div className="w-6 h-6 border-2 border-neon-cyan/30 border-t-neon-cyan rounded-full animate-spin mx-auto mb-2"></div>
                                  <p className="text-white/50 text-sm">Searching drivers...</p>
                                </div>
                              ) : driverSearchResults.length > 0 ? (
                                <>
                                  <div className="px-4 py-2 bg-neon-cyan/10 border-b border-neon-cyan/20">
                                    <p className="text-xs text-neon-cyan font-medium">{driverSearchResults.length} driver(s) found</p>
                                  </div>
                                  {driverSearchResults.map(driver => (
                                    <button
                                      key={driver.id}
                                      type="button"
                                      onClick={() => selectDriver(driver)}
                                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-neon-cyan/10 transition-colors text-left border-b border-white/5 last:border-0"
                                      data-testid={`driver-option-${driver.username}`}
                                    >
                                      <div className="w-10 h-10 rounded-full bg-neon-purple/20 flex items-center justify-center text-neon-purple font-bold flex-shrink-0">
                                        {driver.name.charAt(0).toUpperCase()}
                                      </div>
                                      <div className="flex-1 min-w-0">
                                        <p className="font-medium text-white truncate">{driver.name}</p>
                                        <p className="text-xs text-neon-cyan">@{driver.username}</p>
                                      </div>
                                      <Check className="w-5 h-5 text-neon-cyan opacity-0 group-hover:opacity-100" />
                                    </button>
                                  ))}
                                </>
                              ) : (
                                <div className="px-4 py-6 text-center">
                                  <User className="w-8 h-8 text-white/20 mx-auto mb-2" />
                                  <p className="text-white/50 text-sm">No drivers found for "{driverSearchQuery}"</p>
                                  <p className="text-white/30 text-xs mt-1">Try a different username</p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                      
                      <p className="text-xs text-white/40 mt-2">
                        {selectedDriver 
                          ? `${selectedDriver.name} will be notified and can accept or decline your request.`
                          : "Leave empty to let any available driver accept your ride."
                        }
                      </p>
                    </div>

                    {/* Recurring Ride Option */}
                    <div className="mt-4 p-4 bg-purple-500/10 border border-purple-500/20 rounded-xl">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={requestForm.is_recurring}
                          onChange={(e) => setRequestForm({ ...requestForm, is_recurring: e.target.checked })}
                          className="w-5 h-5 rounded border-white/20 bg-dark-subtle text-purple-500 focus:ring-purple-500"
                        />
                        <div>
                          <span className="font-medium text-purple-400 flex items-center gap-2">
                            <Repeat className="w-4 h-4" />
                            Make this a recurring ride
                          </span>
                          <span className="text-xs text-white/50 block mt-0.5">Set up a regular schedule for this route</span>
                        </div>
                      </label>

                      {/* Recurring Options */}
                      {requestForm.is_recurring && (
                        <div className="mt-4 space-y-4 pt-4 border-t border-purple-500/20">
                          {/* Days of Week */}
                          <div>
                            <label className="block text-sm mb-2 text-white/70">Repeat on days:</label>
                            <div className="flex flex-wrap gap-2">
                              {[0, 1, 2, 3, 4, 5, 6].map(day => (
                                <button
                                  key={day}
                                  type="button"
                                  onClick={() => toggleRecurrenceDay(day)}
                                  className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${
                                    requestForm.recurrence_days.includes(day)
                                      ? 'bg-purple-500 text-white'
                                      : 'bg-white/10 text-white/60 hover:bg-white/20'
                                  }`}
                                >
                                  {getDayName(day)}
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* End Condition */}
                          <div>
                            <label className="block text-sm mb-2 text-white/70">Ends:</label>
                            <div className="space-y-2">
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="radio"
                                  name="recurrence_end"
                                  checked={requestForm.recurrence_end_type === 'never'}
                                  onChange={() => setRequestForm({ ...requestForm, recurrence_end_type: 'never' })}
                                  className="text-purple-500 focus:ring-purple-500"
                                />
                                <span className="text-sm">Never (until cancelled)</span>
                              </label>
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="radio"
                                  name="recurrence_end"
                                  checked={requestForm.recurrence_end_type === 'date'}
                                  onChange={() => setRequestForm({ ...requestForm, recurrence_end_type: 'date' })}
                                  className="text-purple-500 focus:ring-purple-500"
                                />
                                <span className="text-sm">On date:</span>
                                {requestForm.recurrence_end_type === 'date' && (
                                  <input
                                    type="date"
                                    value={requestForm.recurrence_end_date}
                                    onChange={(e) => setRequestForm({ ...requestForm, recurrence_end_date: e.target.value })}
                                    min={new Date().toISOString().split('T')[0]}
                                    className="bg-dark-subtle border border-white/10 rounded-lg px-2 py-1 text-sm"
                                  />
                                )}
                              </label>
                              <label className="flex items-center gap-2 cursor-pointer">
                                <input
                                  type="radio"
                                  name="recurrence_end"
                                  checked={requestForm.recurrence_end_type === 'occurrences'}
                                  onChange={() => setRequestForm({ ...requestForm, recurrence_end_type: 'occurrences' })}
                                  className="text-purple-500 focus:ring-purple-500"
                                />
                                <span className="text-sm">After</span>
                                {requestForm.recurrence_end_type === 'occurrences' && (
                                  <>
                                    <input
                                      type="number"
                                      min="1"
                                      max="100"
                                      value={requestForm.recurrence_occurrences}
                                      onChange={(e) => setRequestForm({ ...requestForm, recurrence_occurrences: parseInt(e.target.value) || 1 })}
                                      className="w-16 bg-dark-subtle border border-white/10 rounded-lg px-2 py-1 text-sm text-center"
                                    />
                                    <span className="text-sm">rides</span>
                                  </>
                                )}
                              </label>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="relative">
                  <label className="block text-sm mb-2 text-white/80 flex items-center justify-between">
                    <span>Pickup Address</span>
                    <button
                      type="button"
                      onClick={handleLocateMe}
                      className="text-xs text-neon-cyan hover:text-[#00B8D4] flex items-center gap-1"
                      data-testid="locate-me-btn"
                    >
                      <Locate className="w-3 h-3" />
                      Locate Me
                    </button>
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40 z-10" />
                    <input
                      type="text"
                      required
                      value={requestForm.pickup_address}
                      onChange={(e) => handlePickupChange(e.target.value)}
                      onFocus={() => requestForm.pickup_address.length >= 3 && setShowPickupSuggestions(true)}
                      className="w-full bg-dark-subtle border-transparent focus:border-neon-cyan focus:ring-1 focus:ring-neon-cyan rounded-xl p-3 pl-10 text-white"
                      placeholder="Search pickup location..."
                      data-testid="input-pickup-address"
                      autoComplete="off"
                    />
                  </div>
                  {showPickupSuggestions && pickupSuggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-2 bg-dark-surface border border-white/10 rounded-xl overflow-hidden shadow-xl max-h-60 overflow-y-auto">
                      {pickupSuggestions.map((suggestion, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => selectPickupSuggestion(suggestion)}
                          className="w-full text-left px-4 py-3 hover:bg-white/5 transition-colors border-b border-white/5 last:border-0"
                          data-testid={`pickup-suggestion-${idx}`}
                        >
                          <div className="flex items-start gap-2">
                            <MapPin className="w-4 h-4 text-neon-cyan mt-1 flex-shrink-0" />
                            <span className="text-sm text-white/90">{suggestion.display_name}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Add Stops Section - Between Pickup and Dropoff */}
                <div className="space-y-3">
                  {requestForm.stops.map((stop, index) => (
                    <div key={index} className="relative">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 relative">
                          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-neon-purple/20 flex items-center justify-center text-xs text-neon-purple z-10">
                            {index + 1}
                          </div>
                          <input
                            type="text"
                            value={stop.address}
                            onChange={(e) => handleStopChange(index, e.target.value)}
                            onFocus={() => stop.address.length >= 3 && setShowStopSuggestions(true) && setActiveStopIndex(index)}
                            className="w-full bg-dark-subtle border-transparent focus:border-neon-purple focus:ring-1 focus:ring-neon-purple rounded-xl p-3 pl-12 text-white"
                            placeholder={`Stop ${index + 1} address...`}
                            data-testid={`input-stop-${index}`}
                            autoComplete="off"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => removeStop(index)}
                          className="w-10 h-10 rounded-xl bg-red-500/20 hover:bg-red-500/30 flex items-center justify-center text-red-400 transition-colors"
                          data-testid={`remove-stop-${index}`}
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                      {/* Stop suggestions dropdown */}
                      {showStopSuggestions && activeStopIndex === index && stopSuggestions.length > 0 && (
                        <div className="absolute z-50 w-full mt-2 bg-dark-surface border border-white/10 rounded-xl overflow-hidden shadow-xl max-h-60 overflow-y-auto">
                          {stopSuggestions.map((suggestion, idx) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => selectStopSuggestion(suggestion, index)}
                              className="w-full text-left px-4 py-3 hover:bg-white/5 transition-colors border-b border-white/5 last:border-0"
                            >
                              <div className="flex items-start gap-2">
                                <MapPin className="w-4 h-4 text-neon-purple mt-1 flex-shrink-0" />
                                <span className="text-sm text-white/90">{suggestion.display_name}</span>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {/* Add Stop Button */}
                  <button
                    type="button"
                    onClick={addStop}
                    className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-white/20 hover:border-neon-purple/50 rounded-xl text-white/60 hover:text-neon-purple transition-colors"
                    data-testid="add-stop-btn"
                  >
                    <Plus className="w-4 h-4" />
                    <span className="text-sm">Add Stop</span>
                  </button>
                </div>

                <div className="relative">
                  <label className="block text-sm mb-2 text-white/80">Dropoff Address</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40 z-10" />
                    <input
                      type="text"
                      required
                      value={requestForm.dropoff_address}
                      onChange={(e) => handleDropoffChange(e.target.value)}
                      onFocus={() => requestForm.dropoff_address.length >= 3 && setShowDropoffSuggestions(true)}
                      className="w-full bg-dark-subtle border-transparent focus:border-neon-cyan focus:ring-1 focus:ring-neon-cyan rounded-xl p-3 pl-10 text-white"
                      placeholder="Search dropoff location..."
                      data-testid="input-dropoff-address"
                      autoComplete="off"
                    />
                  </div>
                  {showDropoffSuggestions && dropoffSuggestions.length > 0 && (
                    <div className="absolute z-50 w-full mt-2 bg-dark-surface border border-white/10 rounded-xl overflow-hidden shadow-xl max-h-60 overflow-y-auto">
                      {dropoffSuggestions.map((suggestion, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => selectDropoffSuggestion(suggestion)}
                          className="w-full text-left px-4 py-3 hover:bg-white/5 transition-colors border-b border-white/5 last:border-0"
                          data-testid={`dropoff-suggestion-${idx}`}
                        >
                          <div className="flex items-start gap-2">
                            <Navigation className="w-4 h-4 text-neon-purple mt-1 flex-shrink-0" />
                            <span className="text-sm text-white/90">{suggestion.display_name}</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Leg Details & Price Breakdown */}
                {legDetails.length > 0 && (
                  <div className="bg-dark-subtle rounded-xl p-4 space-y-3">
                    <h4 className="text-sm font-semibold text-white/90 flex items-center gap-2">
                      <Route className="w-4 h-4 text-neon-cyan" />
                      Route Breakdown
                    </h4>
                    {legDetails.map((leg, index) => (
                      <div key={index} className="flex items-center justify-between py-2 border-b border-white/10 last:border-0">
                        <div className="flex-1">
                          <div className="text-xs text-white/60 truncate max-w-[200px]">
                            Leg {leg.leg}: {leg.from_address.split(',')[0]}
                          </div>
                          <div className="text-xs text-white/40">
                            → {leg.to_address.split(',')[0]}
                          </div>
                          <div className="text-xs text-neon-cyan mt-1">
                            {leg.distance} mi
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-white/60">$</span>
                          <input
                            type="number"
                            min="1"
                            value={requestForm.leg_fares[index] || leg.fare}
                            onChange={(e) => updateLegFare(index, e.target.value)}
                            className="w-16 bg-dark-surface border border-white/10 rounded-lg p-1 text-center text-white text-sm"
                            data-testid={`leg-fare-${index}`}
                          />
                        </div>
                      </div>
                    ))}
                    <div className="flex justify-between items-center pt-2 border-t border-white/20">
                      <span className="text-sm font-semibold text-white">Total</span>
                      <div className="text-right">
                        <div className="text-lg font-bold text-neon-cyan">
                          ${legDetails.reduce((sum, leg) => sum + (requestForm.leg_fares[leg.leg - 1] || leg.fare), 0)}
                        </div>
                        <div className="text-xs text-white/60">
                          {legDetails.reduce((sum, leg) => sum + leg.distance, 0).toFixed(1)} mi total
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Seats Required */}
                <div>
                  <label className="block text-sm mb-2 text-white/80">Number of Seats Required</label>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setRequestForm({ ...requestForm, seats_requested: Math.max(1, requestForm.seats_requested - 1) })}
                      className="w-12 h-12 rounded-xl bg-dark-subtle hover:bg-white/10 flex items-center justify-center text-2xl font-bold transition-colors"
                      data-testid="seats-decrease-btn"
                    >
                      -
                    </button>
                    <div className="flex-1 text-center">
                      <div className="text-3xl font-bold text-neon-cyan">{requestForm.seats_requested}</div>
                      <div className="text-xs text-white/60">{requestForm.seats_requested === 1 ? 'seat' : 'seats'}</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setRequestForm({ ...requestForm, seats_requested: Math.min(8, requestForm.seats_requested + 1) })}
                      className="w-12 h-12 rounded-xl bg-dark-subtle hover:bg-white/10 flex items-center justify-center text-2xl font-bold transition-colors"
                      data-testid="seats-increase-btn"
                    >
                      +
                    </button>
                  </div>
                </div>

                {/* Custom Price */}
                <div>
                  <label className="block text-sm mb-2 text-white/80 flex items-center justify-between">
                    <span>Ride Price ($)</span>
                    {requestForm.custom_price && (
                      <span className="text-xs text-neon-cyan">Editable</span>
                    )}
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neon-cyan z-10" />
                    <input
                      type="number"
                      step="1"
                      min="1"
                      value={requestForm.custom_price}
                      onChange={(e) => setRequestForm({ ...requestForm, custom_price: e.target.value })}
                      placeholder="Enter price..."
                      className="w-full bg-dark-subtle border-transparent focus:border-neon-cyan focus:ring-1 focus:ring-neon-cyan rounded-xl p-3 pl-10 text-white text-lg font-semibold placeholder:text-white/40"
                      data-testid="input-custom-price"
                    />
                  </div>
                  <p className="text-xs text-white/40 mt-2">
                    {requestForm.pickup_lat && requestForm.dropoff_lat ? (
                      <>
                        Estimated distance: {calculateDistance(requestForm.pickup_lat, requestForm.pickup_lng, requestForm.dropoff_lat, requestForm.dropoff_lng).toFixed(1)} miles. 
                        Price auto-calculated. Feel free to adjust if needed.
                      </>
                    ) : (
                      'Enter pickup and dropoff to auto-calculate fare. Default: $1/mile, $3 minimum, +$1 per extra seat.'
                    )}
                  </p>
                </div>

                <div>
                  <label className="block text-sm mb-2 text-white/80">Notes (Optional)</label>
                  <textarea
                    value={requestForm.notes}
                    onChange={(e) => setRequestForm({ ...requestForm, notes: e.target.value })}
                    className="w-full bg-dark-subtle border-transparent focus:border-neon-cyan focus:ring-1 focus:ring-neon-cyan rounded-xl p-3 text-white"
                    rows="2"
                    placeholder="Any special instructions?"
                    data-testid="input-notes"
                  />
                </div>
                <button
                  type="submit"
                  className="w-full bg-neon-cyan text-black hover:bg-[#00B8D4] rounded-full font-bold py-4 shadow-[0_0_15px_rgba(0,224,255,0.4)] transition-all hover:scale-105"
                  data-testid="submit-request-btn"
                >
                  {requestForm.ride_type === 'scheduled' ? 'Schedule Ride' : 'Request Ride Now'}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* Active Ride Card */}
        {activeRide && (
          <div className="max-w-2xl mx-auto mt-6">
            <div className="glass-panel rounded-2xl p-6" data-testid="active-ride-card">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-outfit font-bold text-2xl">Current Ride</h2>
                <div className={`font-mono text-lg ${getStatusColor(activeRide.status)}`} data-testid="ride-status">
                  {formatStatus(activeRide.status)}
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-neon-cyan mt-1" />
                  <div>
                    <div className="text-sm text-white/60">Pickup</div>
                    <div className="font-medium">{activeRide.pickup_address}</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Navigation className="w-5 h-5 text-neon-purple mt-1" />
                  <div>
                    <div className="text-sm text-white/60">Dropoff</div>
                    <div className="font-medium">{activeRide.dropoff_address}</div>
                  </div>
                </div>

                {activeRide.driver_name && (
                  <div className="flex items-start gap-3">
                    <User className="w-5 h-5 text-green-400 mt-1" />
                    <div>
                      <div className="text-sm text-white/60">Driver</div>
                      <div className="font-medium">{activeRide.driver_name}</div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-4 gap-4 pt-4 border-t border-white/10">
                  <div>
                    <div className="text-sm text-white/60 mb-1">Distance</div>
                    <div className="font-mono font-bold">{activeRide.distance.toFixed(1)} miles</div>
                  </div>
                  <div>
                    <div className="text-sm text-white/60 mb-1">ETA</div>
                    <div className="font-mono font-bold">{activeRide.eta} min</div>
                  </div>
                  <div>
                    <div className="text-sm text-white/60 mb-1">Seats</div>
                    <div className="font-mono font-bold">{activeRide.seats_requested || 1}</div>
                  </div>
                  <div>
                    <div className="text-sm text-white/60 mb-1">Fare</div>
                    <div className="font-mono font-bold">${activeRide.fare}</div>
                  </div>
                </div>

                {activeRide.driver_name && activeRide.status !== 'completed' && (
                  <button
                    onClick={() => {
                      setShowChat(true);
                      setUnreadMessages(0);
                    }}
                    className="w-full bg-neon-purple/20 text-neon-purple hover:bg-neon-purple/30 rounded-full font-medium py-3 mt-4 transition-colors flex items-center justify-center gap-2 relative"
                    data-testid="open-chat-btn"
                  >
                    <MessageCircle className="w-5 h-5" />
                    Chat with Driver
                    {unreadMessages > 0 && (
                      <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center animate-pulse">
                        {unreadMessages > 9 ? '9+' : unreadMessages}
                      </span>
                    )}
                  </button>
                )}

                {/* Google Maps Navigation Button - Show when ride is accepted */}
                {activeRide.driver_name && activeRide.status !== 'completed' && activeRide.status !== 'cancelled' && (
                  <button
                    onClick={openGoogleMapsNavigation}
                    className="w-full bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 rounded-full font-medium py-3 mt-2 transition-colors flex items-center justify-center gap-2"
                    data-testid="navigate-btn"
                  >
                    <ExternalLink className="w-5 h-5" />
                    View Route in Google Maps
                  </button>
                )}

                {activeRide.status !== 'in_progress' && activeRide.status !== 'completed' && (
                  <button
                    onClick={() => setShowCancelModal(true)}
                    className="w-full bg-red-500/20 text-red-400 hover:bg-red-500/30 rounded-full font-medium py-3 mt-4 transition-colors"
                    data-testid="cancel-ride-btn"
                  >
                    Cancel Ride
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Chat Box - works with active ride or ride from notification center */}
        {showChat && (activeRide?.driver_name || chatRide?.driver_name) && (
          <ChatBox
            ride={chatRide || activeRide}
            socket={socketRef.current}
            currentUserRole="rider"
            onClose={() => {
              setShowChat(false);
              setChatRide(null);
            }}
          />
        )}
      </div>
      {/* End of Main Content */}
      </div>
      {/* End of Scrollable Content Area */}

      {/* Account Settings Modal */}
      {showAccountSettings && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start sm:items-center justify-center p-4 sm:p-6 pt-20 sm:pt-6 overflow-y-auto">
          <div className="glass-panel rounded-2xl p-4 sm:p-6 w-full max-w-md max-h-[85vh] sm:max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4 sm:mb-6 gap-2">
              <h2 className="font-outfit font-bold text-xl sm:text-2xl">Account Settings</h2>
              <div className="flex items-center gap-1 sm:gap-2">
                {!isEditingAccount ? (
                  <button
                    onClick={() => setIsEditingAccount(true)}
                    className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 bg-neon-cyan/20 text-neon-cyan hover:bg-neon-cyan hover:text-black rounded-lg transition-all text-sm"
                    data-testid="edit-account-btn"
                  >
                    <Pencil className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      <span className="hidden xs:inline">Edit</span>
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        setIsEditingAccount(false);
                        // Reset form to original values
                        const names = user.name?.split(' ') || ['', ''];
                        setAccountForm({
                          first_name: names[0] || '',
                          last_name: names.slice(1).join(' ') || '',
                          email: user.email || '',
                          username: user.username || '',
                          phone: user.phone || '',
                          current_password: '',
                          new_password: '',
                          confirm_password: '',
                        });
                      }}
                      className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 bg-white/10 text-white/70 hover:bg-white/20 rounded-lg transition-all text-sm"
                      data-testid="cancel-edit-btn"
                    >
                      <span className="hidden xs:inline">Cancel</span>
                      <X className="w-3.5 h-3.5 xs:hidden" />
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setShowAccountSettings(false);
                      setIsEditingAccount(false);
                    }}
                    className="p-1.5 sm:p-2 hover:bg-white/10 rounded-lg transition-colors"
                  >
                    <X className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                </div>
              </div>

              {/* Read-Only View */}
              {!isEditingAccount ? (
                <div className="space-y-4">
                  {/* Profile Info Display */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 p-4 bg-dark-subtle rounded-xl">
                      <User className="w-5 h-5 text-neon-cyan flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-white/50 mb-1">Full Name</p>
                        <p className="text-white font-medium truncate">{user.name || 'Not set'}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-4 bg-dark-subtle rounded-xl">
                      <AtSign className="w-5 h-5 text-neon-purple flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-white/50 mb-1">Username</p>
                        <p className="text-white font-medium truncate">@{user.username || 'Not set'}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-4 bg-dark-subtle rounded-xl">
                      <Mail className="w-5 h-5 text-blue-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-white/50 mb-1">Email Address</p>
                        <p className="text-white font-medium truncate">{user.email || 'Not set'}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 p-4 bg-dark-subtle rounded-xl">
                      <Phone className="w-5 h-5 text-green-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-white/50 mb-1">Phone Number</p>
                        <p className="text-white font-medium truncate">{user.phone || 'Not set'}</p>
                      </div>
                    </div>
                  </div>

                  <p className="text-center text-white/40 text-sm mt-4">
                    Click the Edit button to modify your details
                  </p>
                </div>
              ) : (
                /* Edit Mode */
                <div className="space-y-4">
                  {/* Name Section */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm mb-2 text-white/80">First Name *</label>
                      <input
                        type="text"
                        required
                        value={accountForm.first_name}
                        onChange={(e) => setAccountForm({...accountForm, first_name: e.target.value})}
                        className="w-full bg-dark-subtle border border-white/10 focus:border-neon-cyan focus:ring-1 focus:ring-neon-cyan rounded-xl p-3 text-white"
                        data-testid="input-first-name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm mb-2 text-white/80">Last Name *</label>
                      <input
                        type="text"
                        required
                        value={accountForm.last_name}
                        onChange={(e) => setAccountForm({...accountForm, last_name: e.target.value})}
                        className="w-full bg-dark-subtle border border-white/10 focus:border-neon-cyan focus:ring-1 focus:ring-neon-cyan rounded-xl p-3 text-white"
                        data-testid="input-last-name"
                      />
                    </div>
                  </div>

                  {/* Contact Information Section */}
                  <div className="pt-4 border-t border-white/10">
                    <p className="text-sm font-medium text-white mb-3">Contact Information</p>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm mb-2 text-white/80">Email Address</label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                          <input
                            type="email"
                            value={accountForm.email}
                            onChange={(e) => setAccountForm({...accountForm, email: e.target.value})}
                            className="w-full bg-dark-subtle border border-white/10 focus:border-neon-cyan focus:ring-1 focus:ring-neon-cyan rounded-xl p-3 pl-10 text-white"
                            placeholder="your@email.com"
                            data-testid="input-email"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm mb-2 text-white/80">Username</label>
                        <div className="relative">
                          <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                          <input
                            type="text"
                            value={accountForm.username}
                            onChange={(e) => setAccountForm({...accountForm, username: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '')})}
                            className="w-full bg-dark-subtle border border-white/10 focus:border-neon-cyan focus:ring-1 focus:ring-neon-cyan rounded-xl p-3 pl-10 text-white"
                            placeholder="username"
                            data-testid="input-username"
                          />
                        </div>
                        <p className="text-xs text-white/40 mt-1">Letters, numbers, and underscores only</p>
                      </div>
                      <div>
                        <label className="block text-sm mb-2 text-white/80">Phone Number</label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                          <input
                            type="tel"
                            value={accountForm.phone}
                            onChange={(e) => setAccountForm({...accountForm, phone: e.target.value.replace(/[^0-9+\-\s]/g, '')})}
                            className="w-full bg-dark-subtle border border-white/10 focus:border-neon-cyan focus:ring-1 focus:ring-neon-cyan rounded-xl p-3 pl-10 text-white"
                            placeholder="Your phone number"
                            data-testid="input-phone"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Password Required Section */}
                  <div className="pt-4 border-t border-white/10">
                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3 mb-3">
                      <p className="text-sm text-yellow-400">Current password required to save any changes</p>
                    </div>
                    <input
                      type="password"
                      placeholder="Enter current password *"
                      value={accountForm.current_password}
                      onChange={(e) => setAccountForm({...accountForm, current_password: e.target.value})}
                      className="w-full bg-dark-subtle border border-white/10 focus:border-neon-cyan focus:ring-1 focus:ring-neon-cyan rounded-xl p-3 text-white placeholder:text-white/40"
                      data-testid="input-current-password"
                    />
                  </div>

                  {/* Change Password Section */}
                  <div className="pt-4 border-t border-white/10">
                    <p className="text-sm text-white/60 mb-3">Change Password (Optional)</p>
                    <div className="space-y-3">
                      <input
                        type="password"
                        placeholder="New Password (min 6 characters)"
                        value={accountForm.new_password}
                        onChange={(e) => setAccountForm({...accountForm, new_password: e.target.value})}
                        className="w-full bg-dark-subtle border border-white/10 focus:border-neon-cyan focus:ring-1 focus:ring-neon-cyan rounded-xl p-3 text-white placeholder:text-white/40"
                        data-testid="input-new-password"
                      />
                      <input
                        type="password"
                        placeholder="Confirm New Password"
                        value={accountForm.confirm_password}
                        onChange={(e) => setAccountForm({...accountForm, confirm_password: e.target.value})}
                        className="w-full bg-dark-subtle border border-white/10 focus:border-neon-cyan focus:ring-1 focus:ring-neon-cyan rounded-xl p-3 text-white placeholder:text-white/40"
                        data-testid="input-confirm-password"
                      />
                    </div>
                  </div>

                  <button
                    onClick={handleUpdateAccount}
                    className="w-full bg-neon-cyan text-black hover:bg-[#00B8D4] rounded-full font-bold py-4 shadow-[0_0_15px_rgba(0,224,255,0.4)] transition-all hover:scale-105 flex items-center justify-center gap-2"
                    data-testid="save-account-btn"
                  >
                    <Check className="w-5 h-5" />
                    Save Changes
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Cancel Ride Modal */}
        {showCancelModal && activeRide && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6">
            <div className="glass-panel rounded-2xl p-6 w-full max-w-md">
              <h2 className="font-outfit font-bold text-2xl mb-4">Cancel Ride</h2>
              <p className="text-white/60 mb-4">Please provide a reason for cancellation:</p>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="e.g., Found another ride, Plans changed..."
                className="w-full bg-dark-subtle border-transparent focus:border-neon-cyan focus:ring-1 focus:ring-neon-cyan rounded-xl p-3 text-white placeholder:text-white/40 mb-4"
                rows="3"
                data-testid="cancel-reason-input"
              />
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowCancelModal(false);
                    setCancelReason('');
                  }}
                  className="flex-1 bg-white/10 hover:bg-white/20 rounded-full py-3 transition-colors"
                  data-testid="keep-ride-btn"
                >
                  Keep Ride
                </button>
                <button
                  onClick={handleCancelRide}
                  disabled={!cancelReason.trim()}
                  className="flex-1 bg-red-500 hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-full font-bold py-3 transition-all"
                  data-testid="confirm-cancel-btn"
                >
                  Cancel Ride
                </button>
              </div>
            </div>
          </div>
        )}


        {/* Scheduled Rides Modal */}
        {showRecurringRides && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center p-4 pt-16 overflow-y-auto">
            <div className="glass-panel rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
              {/* Header */}
              <div className="p-4 sm:p-6 border-b border-white/10 flex-shrink-0">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-6 h-6 text-purple-400" />
                    <h2 className="font-outfit font-bold text-xl sm:text-2xl">Scheduled Rides</h2>
                  </div>
                  <button
                    onClick={() => setShowRecurringRides(false)}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <p className="text-white/60 text-sm mt-2">
                  View and manage your scheduled and recurring ride requests
                </p>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-4 sm:p-6">
                {recurringRides.length === 0 ? (
                  <div className="text-center py-12">
                    <Calendar className="w-16 h-16 text-white/20 mx-auto mb-4" />
                    <p className="text-white/60 font-medium">No scheduled rides yet</p>
                    <p className="text-white/40 text-sm mt-2 max-w-sm mx-auto">
                      When you request a scheduled ride, it will appear here. You can also set up recurring rides for regular trips.
                    </p>
                    <button
                      onClick={() => {
                        setShowRecurringRides(false);
                        setShowRequestForm(true);
                        setRequestForm(prev => ({ ...prev, ride_type: 'scheduled' }));
                      }}
                      className="mt-6 bg-purple-500 hover:bg-purple-600 text-white rounded-xl px-6 py-3 font-medium transition-colors"
                    >
                      Schedule a Ride
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Active Scheduled Rides */}
                    <div>
                      <h3 className="text-sm font-medium text-white/60 mb-3">Upcoming Rides</h3>
                      <div className="space-y-3">
                        {recurringRides.map((ride) => (
                          <div
                            key={ride.id}
                            className="glass-panel rounded-xl p-4 border border-white/10"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                {/* Time & Status */}
                                <div className="flex items-center gap-2 mb-2">
                                  <Clock className="w-4 h-4 text-purple-400" />
                                  <span className="font-medium">
                                    {ride.scheduled_time 
                                      ? new Date(ride.scheduled_time).toLocaleString([], { 
                                          weekday: 'short',
                                          month: 'short', 
                                          day: 'numeric',
                                          hour: '2-digit', 
                                          minute: '2-digit' 
                                        })
                                      : 'Flexible time'}
                                  </span>
                                  {ride.is_recurring && (
                                    <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded-full flex items-center gap-1">
                                      <Repeat className="w-3 h-3" />
                                      Recurring
                                    </span>
                                  )}
                                </div>
                                
                                {/* Locations */}
                                <div className="space-y-1 text-sm">
                                  <div className="flex items-start gap-2">
                                    <MapPin className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                                    <span className="text-white/80 truncate">{ride.pickup_address}</span>
                                  </div>
                                  <div className="flex items-start gap-2">
                                    <Navigation className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                                    <span className="text-white/80 truncate">{ride.dropoff_address}</span>
                                  </div>
                                </div>
                                
                                {/* Fare & Status */}
                                <div className="flex items-center gap-4 mt-3 text-sm">
                                  <div className="flex items-center gap-1 text-green-400">
                                    <DollarSign className="w-4 h-4" />
                                    <span className="font-medium">{ride.custom_fare || ride.fare}</span>
                                  </div>
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                                    ride.status === 'accepted' ? 'bg-green-500/20 text-green-400' :
                                    ride.status === 'requested' ? 'bg-yellow-500/20 text-yellow-400' :
                                    'bg-white/10 text-white/60'
                                  }`}>
                                    {ride.status === 'accepted' ? 'Driver Assigned' :
                                     ride.status === 'requested' ? 'Finding Driver' :
                                     ride.status}
                                  </span>
                                </div>

                                {/* Recurrence Info */}
                                {ride.is_recurring && ride.recurrence_days && (
                                  <div className="mt-2 text-xs text-white/50">
                                    Repeats: {ride.recurrence_days.map(d => getDayName(d)).join(', ')}
                                  </div>
                                )}
                              </div>
                              
                              {/* Actions */}
                              <div className="flex flex-col gap-2">
                                <button
                                  onClick={() => deleteRecurringRide(ride.id)}
                                  className="p-2 hover:bg-red-500/20 rounded-lg transition-colors text-red-400"
                                  title="Cancel ride"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-white/10 flex-shrink-0">
                <button
                  onClick={() => {
                    setShowRecurringRides(false);
                    setShowRequestForm(true);
                    setRequestForm(prev => ({ ...prev, ride_type: 'scheduled' }));
                  }}
                  className="w-full bg-purple-500 hover:bg-purple-600 text-white rounded-xl py-3 font-medium transition-colors flex items-center justify-center gap-2"
                >
                  <Calendar className="w-5 h-5" />
                  Schedule New Ride
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Ride History Sidebar */}
        {showHistory && (
          <div className="fixed top-0 right-0 h-full w-full max-w-md bg-dark-surface border-l border-white/10 z-50 overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-outfit font-bold text-2xl">Ride History</h2>
                <button
                  onClick={() => setShowHistory(false)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  data-testid="close-history-btn"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-3">
                {rideHistory.length === 0 ? (
                  <div className="text-center py-10 text-white/60">No rides yet</div>
                ) : (
                  rideHistory.map((ride, idx) => (
                    <div key={ride.id} className="bg-dark-bg rounded-xl p-4" data-testid={`history-ride-${idx}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className={`text-sm font-mono ${getStatusColor(ride.status)}`}>
                          {formatStatus(ride.status)}
                        </div>
                        <div className="text-sm text-white/60">${ride.fare}</div>
                      </div>
                      <div className="text-sm text-white/80">
                        <div className="truncate">{ride.pickup_address}</div>
                        <div className="text-white/60">→</div>
                        <div className="truncate">{ride.dropoff_address}</div>
                      </div>
                      <div className="text-xs text-white/40 mt-2">
                        {new Date(ride.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}
    </div>
  );
}
